import type { DateArg } from "date-fns";
import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import type { QendoIconSet } from "~/components/QendoIcon";

export enum FileCategory {
    image = "image",
    text = "text",
    file = "file",
}

export type DocumentMimeType =
    | "application/msword"
    | "application/pdf"
    | "application/vnd.oasis.opendocument.text"
    | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    | "image/jpeg"
    | "image/jpg"
    | "image/png"
    | "image/bmp"
    | "image/gif"
    | "image/webp"
    | "text/plain"
    | "text/csv";

export const DocumentMimeTypes: DocumentMimeType[] = [
    "application/msword",
    "application/pdf",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/bmp",
    "image/gif",
    "image/webp",
    "text/plain",
    "text/csv",
];

export type UploadableType = "photographs" | "documents";

export type FileUploadResponse = {
    uuid: string;
    bucket: string;
    key: string;
    url: string;
    headers: Record<string, string>;
};

export const MimeTypeCategoryMap: Record<DocumentMimeType, FileCategory> = {
    "application/msword": FileCategory.file,
    "application/pdf": FileCategory.file,
    "application/vnd.oasis.opendocument.text": FileCategory.file,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileCategory.file,
    "image/jpeg": FileCategory.image,
    "image/jpg": FileCategory.image,
    "image/png": FileCategory.image,
    "image/bmp": FileCategory.image,
    "image/gif": FileCategory.image,
    "image/webp": FileCategory.image,
    "text/plain": FileCategory.text,
    "text/csv": FileCategory.text,
};

export const FileIconMap: Record<FileCategory, QendoIconSet> = {
    [FileCategory.image]: "image",
    [FileCategory.text]: "list",
    [FileCategory.file]: "file",
};

export type FileData<T extends FileCategory = FileCategory> = {
    id: string;
    url: string;
    name: string;
    category: T;
    file_size: string;
    filename: string;
    mime: DocumentMimeType;
};

export type PhotographData = {
    attachments: number;
} & FileData<FileCategory.image>;

export type MediaCreatePayload = {
    id?: never;
    name?: string | null;
    asset: ImagePickerAsset;
};

export type MediaUpdatePayload = {
    id: string;
    name: string;
    asset?: never;
};

export type MediaPayload = MediaCreatePayload | MediaUpdatePayload;

export type ClinicalDocumentData = {
    id: string;
    date: DateArg<Date>;
    pain_plan: boolean;
    name: string;
    description?: string | null;
    search: string[];
    files: FileData[];
};

export type DocumentAsset = ImagePickerAsset | DocumentPickerAsset;

export type CreateDocumentPayload = {
    id?: never;
    name: string;
    document_date: DateArg<Date>;
    pain_plan: boolean;
    description?: string | null;
    assets: DocumentAsset[];
    remove?: never;
};

export type UpdateDocumentPayload = {
    id: string;
    name: string;
    document_date: DateArg<Date>;
    pain_plan: boolean;
    description?: string | null;
    assets: DocumentAsset[];
    remove: string[];
};

export type DocumentPayload = CreateDocumentPayload | UpdateDocumentPayload;
